
import { parseISO, addMinutes, format, isAfter, isBefore } from "date-fns";
import { toZonedTime, formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import { getEnergyForecastSync } from "@/lib/energy";
import { getEnergyTypeForValue, ENERGY_THRESHOLDS } from "@/lib/energy";
import type { EnergyBlockType, EnergyDataPoint } from "@/lib/energy";
import type { AvailabilitySettings, TimeSlot, TimezoneAwareSlot } from "./types";
import { getEnergyLevelForTime } from "./energy-utils";
import { filterAvailableSlots } from "./slot-filters";
import { checkSlotOverlap, checkBufferConflicts } from "./slot-conflicts";
import { parseAvailabilityWindow } from "./time-window-parser";
import { 
  getCachedEnergyLookup, 
  batchProcessEnergyLevels, 
  meetsEnergyRequirement 
} from "@/lib/energy/energy-cache";
import { createPerformanceLogger } from "@/lib/utils/performance-logger";
import { createTimezoneAwareSlot } from "@/lib/timezone/timezone-service";

const logger = createPerformanceLogger('SlotCalculator');

export interface SlotCalculationParams {
  date: Date;
  events: any[];
  settings: AvailabilitySettings;
  energyPreference?: EnergyBlockType;
  duration?: number;
  hostUserId?: string;
  timezone?: string;
  includeUnavailable?: boolean;
  availabilityWindow?: { startTime: string; endTime: string };
  wakeTime?: number;
}

/**
 * Convert energy forecast numbers to EnergyDataPoint objects
 */
function convertToEnergyDataPoints(forecast: number[]): EnergyDataPoint[] {
  return forecast.map((value, index) => ({
    time: `${index.toString().padStart(2, '0')}:00`,
    value: value / 100, // Convert percentage to 0-1 range
    hour: index,
    level: value / 100,
    energy: value,
    timestamp: new Date()
  }));
}

/**
 * Optimized slot calculation with timezone-aware slots
 */
export async function getAvailableTimeSlots({
  date,
  events = [],
  settings,
  energyPreference = "focus",
  duration = 30,
  hostUserId,
  timezone,
  includeUnavailable = false,
  availabilityWindow = { startTime: "09:00", endTime: "17:00" },
  wakeTime = 7
}: SlotCalculationParams): Promise<TimezoneAwareSlot[]> {
  
  return logger.timeAsync('getAvailableTimeSlots', async () => {
    logger.debug(`Slot generation for ${date.toDateString()}, preference: ${energyPreference}`);
    logger.debug(`Using availability window: ${availabilityWindow.startTime} - ${availabilityWindow.endTime}`);
    
    if (!date) {
      logger.warn("Date is undefined, returning empty slots");
      return [];
    }
    
    if (!settings) {
      logger.warn("Settings are undefined, using defaults");
      settings = { bufferBefore: 15, bufferAfter: 15, minNoticeTime: 60 };
    }

    // Use organizer's timezone or UTC as fallback
    const organizerTimezone = timezone || 'UTC';
    
    // Convert date to organizer's timezone
    let zonedDate = date;
    if (timezone) {
      try {
        zonedDate = toZonedTime(date, timezone);
        logger.debug(`Converted date to ${timezone}`);
      } catch (e) {
        logger.error(`Error converting date to timezone ${timezone}:`, e);
        zonedDate = date;
      }
    }
    
    // Process busy events
    const busySlots = logger.time('processBusyEvents', () => {
      return events.map((event, index) => {
        try {
          const startRaw = parseISO(event.start.dateTime || event.start.date);
          const endRaw = parseISO(event.end.dateTime || event.end.date);
          
          const start = timezone ? toZonedTime(startRaw, timezone) : startRaw;
          const end = timezone ? toZonedTime(endRaw, timezone) : endRaw;
          
          return {
            start,
            end,
            summary: event.summary || 'Busy'
          };
        } catch (e) {
          logger.error(`Error parsing event ${index + 1}:`, e);
          return null;
        }
      }).filter(Boolean);
    });
    
    logger.debug(`Processed ${busySlots.length} valid busy events`);
    
    // Get energy forecast using sync version
    let energyForecast: EnergyDataPoint[] = [];
    try {
      const sleepDuration = 8; // Default sleep duration
      const rawForecast = getEnergyForecastSync({ 
        wakeTimeHour: wakeTime, 
        sleepDuration 
      });
      energyForecast = convertToEnergyDataPoints(rawForecast);
      logger.debug(`Fetched ${energyForecast.length} energy forecast points`);
    } catch (err) {
      logger.error("Error fetching energy forecast:", err);
    }

    // Time calculations
    const now = new Date();
    
    // Convert current time to organizer's timezone for proper comparison with slot times
    const zonedNow = timezone ? toZonedTime(now, timezone) : now;
    const minNoticeMinutes = settings?.minNoticeTime || 60;
    const minNoticeDate = addMinutes(zonedNow, minNoticeMinutes);
    
    logger.debug(`Min notice calculation:`, {
      currentTime: now.toISOString(),
      zonedTime: zonedNow.toISOString(),
      minNoticeMinutes: minNoticeMinutes,
      minNoticeDate: minNoticeDate.toISOString(),
      settingsMinNoticeTime: settings?.minNoticeTime,
      settingsObject: settings
    });
    
    // Parse availability window - use the passed availability window
    const { startHour, startMinute, endHour, endMinute } = parseAvailabilityWindow(availabilityWindow);
    
    logger.debug(`Parsed availability window: ${startHour}:${startMinute} - ${endHour}:${endMinute}`);
    
    // Generate time slots for batch processing
    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;
    
    const timeSlots: Date[] = [];
    for (let currentMinutes = startTotalMinutes; currentMinutes < endTotalMinutes; currentMinutes += 30) {
      const hour = Math.floor(currentMinutes / 60);
      const minute = currentMinutes % 60;
      
      if (hour > endHour || (hour === endHour && minute > endMinute)) {
        break;
      }
      
      const slotTime = new Date(zonedDate);
      slotTime.setHours(hour, minute, 0, 0);
      timeSlots.push(slotTime);
    }
    
    logger.debug(`Generated ${timeSlots.length} time slots for processing between ${startHour}:${startMinute} and ${endHour}:${endMinute}`);
    
    // Batch process energy levels
    const energyResults = logger.time('batchProcessEnergy', () => {
      if (energyForecast.length === 0) {
        return timeSlots.map(time => ({
          time,
          energyLevel: 0.5,
          meetsRequirement: true
        }));
      }
      
      const lookupCacheKey = `lookup_${energyForecast.length}_${energyForecast[0]?.time || 0}`;
      const energyLookup = getCachedEnergyLookup(energyForecast, lookupCacheKey);
      
      return batchProcessEnergyLevels(timeSlots, energyLookup, energyPreference);
    });
    
    // Process all slots into timezone-aware slots
    const processedSlots: TimezoneAwareSlot[] = logger.time('processSlots', () => {
      return energyResults.map((energyResult, index) => {
        const slotTime = energyResult.time;
        const slotEndTime = addMinutes(slotTime, duration);
        const formattedTime = format(slotTime, "HH:mm");
        
        // Check constraints
        const isTooSoon = isBefore(slotTime, minNoticeDate);
        const { isOverlapping, overlapEvent } = checkSlotOverlap(slotTime, slotEndTime, busySlots);
        const { isInBuffer, bufferEvent } = checkBufferConflicts(
          slotTime, 
          slotEndTime, 
          busySlots, 
          settings.bufferBefore || 0, 
          settings.bufferAfter || 0
        );
        
        // Determine availability
        let conflictReason: 'calendar' | 'energy' | 'buffer' | 'availability' | undefined = undefined;
        let reasonText = '';
        
        if (isOverlapping) {
          conflictReason = 'calendar';
          reasonText = `Overlaps with ${overlapEvent?.summary || 'calendar event'}`;
        } else if (isInBuffer) {
          conflictReason = 'buffer';
          reasonText = `Within buffer time for ${bufferEvent?.summary || 'another event'}`;
        } else if (!energyResult.meetsRequirement && 
                   energyPreference !== 'ignore' && 
                   energyPreference !== 'any') {
          conflictReason = 'energy';
          reasonText = `Insufficient energy for ${energyPreference} preference (level: ${Math.round(energyResult.energyLevel * 100)}%)`;
        } else if (isTooSoon) {
          conflictReason = 'availability';
          reasonText = `Too soon (within ${minNoticeMinutes} minutes notice time)`;
        }
        
        const isAvailable = !isOverlapping && !isInBuffer && energyResult.meetsRequirement && !isTooSoon;
        const energyBlock: EnergyBlockType = getEnergyTypeForValue(energyResult.energyLevel);
        
        // Create timezone-aware slot
        return createTimezoneAwareSlot(
          formattedTime,
          duration,
          organizerTimezone,
          isAvailable,
          {
            energyLevel: energyResult.energyLevel,
            energyBlock,
            reason: conflictReason ? reasonText : undefined,
            unavailableType: conflictReason,
            overlapEvent: overlapEvent ? overlapEvent.summary : undefined
          }
        );
      });
    });
    
    const availableCount = processedSlots.filter(s => s.isAvailable).length;
    logger.info(`Generated ${processedSlots.length} total slots, ${availableCount} available`);
    
    return includeUnavailable ? processedSlots : processedSlots.filter(slot => slot.isAvailable);
  });
}

// Re-export slot filtering functions for convenience
export { filterAvailableSlots } from './slot-filters';
